import { useEffect, useRef, useState } from "react";
import { Sparkles, PartyPopper } from "lucide-react";

const GOLD_COLORS = [
  "#FFD700",
  "#FFC107",
  "#FFB300",
  "#FFDF00",
  "#F59E0B",
  "#FFEC8B",
];

function createParticle(width, height) {
  const shapes = ["square", "circle", "ribbon", "star"];
  return {
    x: Math.random() * width,
    y: -20 - Math.random() * height * 0.6,
    vx: (Math.random() - 0.5) * 2.2,
    vy: 1.5 + Math.random() * 3,
    size: 8 + Math.random() * 14,
    color: GOLD_COLORS[Math.floor(Math.random() * GOLD_COLORS.length)],
    rotation: Math.random() * Math.PI * 2,
    vRotation: (Math.random() - 0.5) * 0.25,
    shape: shapes[Math.floor(Math.random() * shapes.length)],
    wobble: Math.random() * Math.PI * 2,
    vWobble: 0.04 + Math.random() * 0.06,
  };
}

function drawStar(ctx, size) {
  ctx.beginPath();
  const spikes = 5;
  const outer = size / 2;
  const inner = outer * 0.45;
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (i * Math.PI) / spikes - Math.PI / 2;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

function drawParticle(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rotation);
  ctx.fillStyle = p.color;
  ctx.strokeStyle = "rgba(120, 80, 0, 0.45)";
  ctx.lineWidth = 1.2;
  switch (p.shape) {
    case "circle":
      ctx.beginPath();
      ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    case "ribbon":
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.strokeRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      break;
    case "star":
      drawStar(ctx, p.size);
      ctx.stroke();
      break;
    default:
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      ctx.strokeRect(-p.size / 2, -p.size / 2, p.size, p.size);
  }
  ctx.restore();
}

export default function SaturdayCelebration() {
  const [show, setShow] = useState(false);
  const canvasRef = useRef(null);
  const startedRef = useRef(false);

  // Decide whether to show
  useEffect(() => {
    if (startedRef.current) return;
    const today = new Date();
    if (today.getDay() !== 6) return; // 6 = Saturday

    const dateKey = today.toISOString().slice(0, 10);
    const storageKey = `lumen_sat_celebration_${dateKey}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, "1");
    startedRef.current = true;
    setShow(true);
  }, []);

  // Run animation once shown (canvas now exists in DOM)
  useEffect(() => {
    if (!show) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let particles = [];
    let animId = 0;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    particles = Array.from({ length: 240 }, () =>
      createParticle(window.innerWidth, window.innerHeight)
    );

    const refill = setInterval(() => {
      for (let i = 0; i < 28; i++) {
        particles.push(createParticle(window.innerWidth, window.innerHeight));
      }
      if (particles.length > 420) particles = particles.slice(-380);
    }, 350);

    const TOTAL_MS = 9000;
    const stopAt = performance.now() + TOTAL_MS;

    const tick = (now) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const stopping = now > stopAt;
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.wobble += p.vWobble;
        p.x += p.vx + Math.sin(p.wobble) * 0.7;
        p.y += p.vy;
        p.vy += 0.025;
        p.rotation += p.vRotation;
        if (p.y > window.innerHeight + 40) {
          particles.splice(i, 1);
          continue;
        }
        drawParticle(ctx, p);
      }
      if (stopping && particles.length === 0) return;
      animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);

    const stopRefill = setTimeout(() => clearInterval(refill), TOTAL_MS - 1500);
    const hideTimer = setTimeout(() => setShow(false), 7500);

    return () => {
      cancelAnimationFrame(animId);
      clearInterval(refill);
      clearTimeout(stopRefill);
      clearTimeout(hideTimer);
      window.removeEventListener("resize", resize);
    };
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] pointer-events-none" data-testid="saturday-celebration">
      <canvas ref={canvasRef} className="absolute inset-0" />
      <div className="absolute inset-0 flex items-center justify-center p-6">
        <div
          className="pointer-events-auto max-w-xl w-full rounded-3xl border-2 border-amber-400/60 bg-card/95 backdrop-blur px-6 sm:px-10 py-8 text-center shadow-2xl celebration-pop"
          style={{
            boxShadow:
              "0 25px 60px -15px rgba(255, 193, 7, 0.45), 0 0 0 1px rgba(255, 215, 0, 0.4) inset",
          }}
          data-testid="saturday-message"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Sparkles strokeWidth={1.5} className="size-6" style={{ color: "#F59E0B" }} />
            <span
              className="text-xs font-extrabold uppercase tracking-[0.18em]"
              style={{ color: "#B45309" }}
            >
              You made it to Saturday
            </span>
            <Sparkles strokeWidth={1.5} className="size-6" style={{ color: "#F59E0B" }} />
          </div>
          <div className="flex items-center justify-center mb-4">
            <PartyPopper
              strokeWidth={1.5}
              className="size-12 sm:size-14"
              style={{ color: "#F59E0B" }}
            />
          </div>
          <h2 className="font-display text-3xl sm:text-5xl font-black tracking-tight">
            Congratulations!
          </h2>
          <p className="mt-3 text-base sm:text-lg text-foreground/90 font-medium">
            You wrapped up another week of goals.
            <br className="hidden sm:block" />
            Keep up the good work.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground">
            <span className="size-1.5 rounded-full" style={{ background: "#F59E0B" }} />
            Have a beautiful weekend
            <span className="size-1.5 rounded-full" style={{ background: "#F59E0B" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
